import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Colour from 'components/utils/Colour'
import SocieteCampaignDisclaimerText from '../SocieteCampaignDisclaimerText'
import SocieteCampaignDisclaimerLink from '../SocieteCampaignDisclaimerLink'
import SocieteCampaignDisclaimerTooltip from '../SocieteCampaignDisclaimerTooltip'
import TextLink from 'LuxKit/TextLink'
import LuxuryEscapesStampGrid from 'LuxKit/LuxuryEscapesStampGrid'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import usePersonalPortalHref from 'hooks/usePersonalPortalHref'
import { rem } from 'polished'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import useEmployeeOrders from 'businessTraveller/hooks/useEmployeeOrders'
import { useSocieteInterpolate } from 'loyalty/hooks/useSocieteInterpolate'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import useWhiteLabelAppConfig from 'hooks/useWhiteLabelAppConfig'

const Card = styled.div`
  border-radius: ${rem(8)};
  padding: ${rem(24)};
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

interface Props {
  campaign: App.SocieteCampaignPayload
  targetTier: App.LoyaltyTier
  targetTierDisplayName: string
  societeTierSetting: App.BusinessTraveller.BusinessSettingsSocieteTierConfig
  linkedCreditAccount: App.LoyaltyLinkedCreditAccount | null | undefined
  isAtOrAboveTargetTier: boolean
  employeeSociete: App.BusinessTraveller.EmployeeSociete | undefined
  onLearnMoreClick?: () => void
}

function SocieteCampaignTierCardContent(props: Props) {
  const { campaign, targetTier, targetTierDisplayName, societeTierSetting, linkedCreditAccount, isAtOrAboveTargetTier, employeeSociete, onLearnMoreClick } = props
  const interpolate = useSocieteInterpolate()
  const personalPortalHref = usePersonalPortalHref()
  const { isApp } = useWhiteLabelAppConfig()

  const threshold = societeTierSetting.threshold

  const { total: upcomingTotal, isLoading: isLoadingUpcoming } = useEmployeeOrders({
    timeframe: 'upcoming',
    page: 0,
    limit: 1,
    filterBy: 'traveller',
  })
  const { total: qualifiedTotal, isLoading: isLoadingQualified } = useEmployeeOrders({
    timeframe: 'qualificationWindow',
    page: 0,
    limit: 1,
    filterBy: 'traveller',
  })

  // Societe campaign is completed when the user is at or above the target tier and was recently granted
  const isSocieteCompleted = isAtOrAboveTargetTier && employeeSociete?.isSocieteRecentlyGranted
  const isAlmostThere = !isLoadingQualified && qualifiedTotal === threshold - 1

  const { title, subtitle, highlightText } = useMemo(() => {
    const title = isSocieteCompleted ? campaign.notification.completion.title : campaign.hero.titleHighlight

    const subtitle =
      isSocieteCompleted ? campaign.notification.completion.subtitle
        : isAlmostThere ? campaign.notification.almostThere.secondarySubtitle
          : campaign.notification.unlock.subtitle

    const highlightText = isSocieteCompleted ? `${targetTierDisplayName} status` : ''

    return { title, subtitle, highlightText }
  }, [isSocieteCompleted, isAlmostThere, targetTierDisplayName, campaign.hero.titleHighlight, campaign.notification.completion.title, campaign.notification.completion.subtitle, campaign.notification.almostThere.secondarySubtitle, campaign.notification.unlock.subtitle])

  if (isLoadingUpcoming || isLoadingQualified) {
    return <LoadingBox height={280} borderRadius="L" />
  }

  return <Card className={`tier-${targetTier}`}>
    <VerticalSpacer gap={20}>
      <Group
        direction="horizontal"
        verticalAlign="start"
        gap={4}
      >
        <LuxuryEscapesSmallLogo height={21} fill="currentColor" />
        <LoyaltyProgramNameLogo width={84} height={19} />
      </Group>
      <VerticalSpacer gap={8}>
        {title && <Heading variant="heading3">
          {interpolate(title)}
          {highlightText && <Colour value={`tier-${targetTier}-light-contrast`}>{' '}{highlightText}</Colour>}
        </Heading>}
        <BodyText variant="medium">
          {interpolate(subtitle, {
            qualificationEndDate: (vars) => <BodyText as="span" variant="medium" weight="bold">{vars.qualificationEndDate}</BodyText>,
          })}
        </BodyText>
      </VerticalSpacer>
      {!isSocieteCompleted && <VerticalSpacer gap={12}>
        <LuxuryEscapesStampGrid count={qualifiedTotal} total={threshold} size={48} dashedColour={`tier-${targetTier}-contrast`} dashedOpacity={0.4} />
        <Group direction="horizontal" gap={8} verticalAlign="center">
          <BodyText variant="small">
            <BodyText as="span" variant="small" weight="bold">{upcomingTotal}</BodyText> pending
          </BodyText>
          <BodyText variant="small" weight="bold">•</BodyText>
          <BodyText variant="small">
            <BodyText as="span" variant="small" weight="bold">{qualifiedTotal}</BodyText> completed
          </BodyText>
        </Group>
      </VerticalSpacer>}
      {!isApp && <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center" gap={8}>
        {/* No linked credit account → user isn't a Société member yet */}
        {!linkedCreditAccount && <Group direction="horizontal" verticalAlign="center" gap={4}>
          <SocieteCampaignDisclaimerText>
            Join{' '}
            <SocieteCampaignDisclaimerLink
              text="Société"
              colour={`tier-${targetTier}-contrast`}
              href={`${personalPortalHref}/societe`}
            />{' '}
            to enjoy this benefit
          </SocieteCampaignDisclaimerText>
          <SocieteCampaignDisclaimerTooltip />
        </Group>}
        <TextLink size="small" colour={`tier-${targetTier}-contrast`} to={`/${LOYALTY_LANDING_PAGE}`} onClick={onLearnMoreClick} style={{ marginLeft: 'auto' }}>Learn more</TextLink>
      </Group>}
    </VerticalSpacer>
  </Card>
}

export default SocieteCampaignTierCardContent
